import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

interface SlideContent {
  label: string;
  headline: string;
  subheadline: string;
  bullets: string[];
  primaryCta: { text: string; to: string };
  secondaryCta: { text: string; href: string };
  microText: string;
}

const SLIDES: SlideContent[] = [
  {
    label: "LOT SIZE CALCULATOR",
    headline: "A Lot Size Calculator Built for Prop Traders",
    subheadline: "Accurately size trades using real prop firm drawdown rules\nbefore you place an order.",
    bullets: [
      "Avoid sizing mistakes across different prop firm rules",
      "Instantly see risk, lot size, and drawdown impact before entry",
      "Designed for evaluation and funded account rules",
    ],
    primaryCta: { text: "Use Lot Calculator", to: "/tools/lot-calculator" },
    secondaryCta: { text: "How it works", href: "#how-it-works" },
    microText: "Designed for evaluation and funded accounts.",
  },
];

const AUTO_ROTATE_INTERVAL = 6000;

export function HeroCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, AUTO_ROTATE_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
    setTouchStart(null);
  };

  const slide = SLIDES[activeIndex];

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
        {/* Left column - Copy */}
        <div className="text-center lg:text-left">
          <span className="text-xs font-medium text-yellow-400 uppercase tracking-widest">
            {slide.label}
          </span>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-yellow-400">
            {slide.headline}
          </h1>
          <p className="mt-3 text-sm sm:text-base lg:text-lg text-neutral-200">
            {slide.subheadline}
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <Link
              to={slide.primaryCta.to}
              className="bg-yellow-400 text-black px-5 py-3 rounded-xl font-semibold hover:bg-yellow-300 transition text-center text-sm"
            >
              {slide.primaryCta.text}
            </Link>
            <a
              href={slide.secondaryCta.href}
              className="bg-neutral-950 hover:bg-neutral-900 text-neutral-200 border border-neutral-800 px-5 py-3 rounded-xl text-sm font-semibold transition text-center"
            >
              {slide.secondaryCta.text}
            </a>
          </div>

          <div className="mt-3 text-xs text-neutral-400">{slide.microText}</div>
        </div>

        {/* Right column - Preview card */}
        <div className="lg:pt-1">
          <div className="rounded-2xl border border-neutral-800 bg-neutral-950/30 p-5">
            <div className="text-lg font-bold text-yellow-300">Why traders use PropViper</div>
            <div className="mt-4 space-y-3 text-sm text-neutral-200">
              {slide.bullets.map((bullet, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
                  <div>{bullet}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 relative rounded-2xl border border-neutral-800 bg-neutral-950/40 p-4 overflow-hidden min-h-[200px]">
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-300">Account Size</label>
                  <input
                    value="$100,000"
                    disabled
                    className="mt-1 w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-sm text-neutral-100 opacity-70"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-300">Drawdown Type</label>
                  <input
                    value="Trailing"
                    disabled
                    className="mt-1 w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-sm text-neutral-100 opacity-70"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-300">Risk %</label>
                  <input
                    value="1%"
                    disabled
                    className="mt-1 w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-sm text-neutral-100 opacity-70"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-300">Result</label>
                  <div className="mt-1 rounded-xl border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-yellow-300 font-semibold opacity-80">
                    0.50 lots
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="text-xs text-neutral-200">Full calculator opens instantly.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators - only show if multiple slides */}
      {SLIDES.length > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === activeIndex ? "w-6 bg-yellow-400" : "w-2 bg-neutral-600 hover:bg-neutral-500"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default HeroCarousel;
